import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

const SuccessBanner = ({ email, onClose }) => (
  <Alert
    icon={<CheckCircleIcon fontSize="inherit" />}
    severity="success"
    onClose={onClose}
    sx={{ borderRadius: 2, alignItems: "center" }}
  >
    <Typography fontWeight={700} component="span">
      Invitation successfully sent!
    </Typography>
    <Typography component="div" variant="body2">
      We sent this invite: {email}
    </Typography>
  </Alert>
);

const isValidEmail = (v) => {
  // RFC5322-friendly, pragmatic validator (no unicode local-part)
  const re =
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return re.test(v.trim());
};

export default function ReferPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastSent, setLastSent] = useState("");
  const [copied, setCopied] = useState(false); // keep if you use the snackbar elsewhere
  const { token, user } = useAuthContext();

  const handleSend = async () => {
    const value = email.trim();

    if (!value) {
      setError("Please enter an email.");
      return;
    }
    if (!isValidEmail(value)) {
      setError("Enter a valid email address.");
      return;
    }

    setSending(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/referrals/invite`,
        { email: value, user },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSending(false);
      setShowSuccess(true);
      setLastSent(value);
      setEmail("");
    } catch (err) {
      setSending(false);
      setError("Failed to send the invite. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "," || e.key === " ") {
      // prevent accidental multi or trailing whitespace
      e.preventDefault();
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, maxWidth: 1300, mx: "auto", mt: "70px" }}>
      <Card
        sx={{
          overflow: "hidden",
          mb: 3,
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(59,92,251,0.06) 0%, rgba(59,92,251,0.14) 100%)",
        }}
        elevation={0}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              display: "grid",
              placeItems: "center",
              bgcolor: "#3B5CFB",
              color: "#fff",
              py: 6,
            }}
          >
            <Stack spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 4,
                  bgcolor: "rgba(255,255,255,0.15)",
                  display: "grid",
                  placeItems: "center",
                  boxShadow: 3,
                }}
              >
                <CardGiftcardIcon style={{ fontSize: 43 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} textAlign="center">
                Give 1 month, get 1 month
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Refer a friend
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share 3D Practice with your friends and they’ll get a <b>1 month</b> credit when they sign up.
                You’ll get a <b>1 month credit</b> too. Everybody wins!
              </Typography>

              {showSuccess && (
                <Box sx={{ mb: 2 }}>
                  <SuccessBanner email={lastSent} onClose={() => setShowSuccess(false)} />
                </Box>
              )}

              {/* Invite by single email */}
              <Box sx={{ width: "100%", mb: 2 }}>
                <TextField
                  type="email"
                  fullWidth
                  label="Invite a friend by email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  onKeyDown={handleKeyDown}
                  error={Boolean(error)}
                  helperText={error || "Press Enter or click Send to invite"}
                  InputProps={{
                    endAdornment: (
                      <Box
                        sx={{
                          ml: 1,
                          height: "100%",
                          display: "flex",
                          alignItems: "stretch",
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<EmailIcon />}
                          onClick={handleSend}
                          disabled={sending || !email.trim()}
                          sx={{
                            height: "100%",
                            whiteSpace: "nowrap",
                            bgcolor: "#3B5CFB",
                            "&:hover": { bgcolor: "#2F49C8" },
                          }}
                        >
                          {sending ? "Sending…" : "Send invite"}
                        </Button>
                      </Box>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 56,
                      alignItems: "center",
                    },
                    "& .MuiInputAdornment-root": { height: "100%" },
                  }}
                />
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>

      <Snackbar
        open={copied}
        autoHideDuration={1200}
        onClose={() => setCopied(false)}
        message="Link copied"
      />
    </Box>
  );
}
